import React, { useState, useContext, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Box,
  Chip,
  CircularProgress,
  Divider,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

import axios from '../../../sharedModules/utils/axios.utils';
import { SnackBarContext } from '../../../sharedModules/form/contexts/SnackBarContext';
import { markDeviceSynced, markLogSynced, isJobGroupCached } from '../../utils/backflowCache';

interface SubmitCachedDataModalProps {
  open: boolean;
  onClose: () => void;
  jobGroupId: string;
  jobGroupData: any;
  onSubmitSuccess: () => void;
}

interface SubmitState {
  loading: boolean;
  success: boolean;
  error: string | null;
}

const SubmitCachedDataModal: React.FC<SubmitCachedDataModalProps> = ({
  open,
  onClose,
  jobGroupId,
  jobGroupData,
  onSubmitSuccess,
}) => {
  const history = useHistory();
  const { setSnackBarOpen, setSnackBarMessage, handleSnackbarAlertSeverityChange } = useContext(SnackBarContext);

  const [deviceSubmitStates, setDeviceSubmitStates] = useState<{ [key: string]: SubmitState }>({});
  const [logSubmitStates, setLogSubmitStates] = useState<{ [key: string]: SubmitState }>({});

  // Initialize submit states based on cached data when modal opens
  useEffect(() => {
    if (open && jobGroupData?.jobItems) {
      const initialDeviceStates: { [key: string]: SubmitState } = {};
      const initialLogStates: { [key: string]: SubmitState } = {};

      jobGroupData.jobItems.forEach((item: any) => {
        const itemId = item.id.toString();

        // If log doesn't need sync, mark as successfully submitted
        if (item.backflow_log && !item.backflow_log.needsSync) {
          initialLogStates[itemId] = { loading: false, success: true, error: null };
        }
      });

      setDeviceSubmitStates(initialDeviceStates);
      setLogSubmitStates(initialLogStates);
    } else if (!open) {
      // Reset states when modal closes
      setDeviceSubmitStates({});
      setLogSubmitStates({});
    }
  }, [open, jobGroupData]);

  // Check if an item needs device sync
  const needsDeviceSync = (item: any) => item.needsSync;

  // Check if an item needs log sync
  const needsLogSync = (item: any) => item.backflow_log?.needsSync;

  // Navigate to edit page in offline-only mode
  const handleEditItem = (itemId: number) => {
    history.push(`/backflow_tests/backflow_form/${itemId}?offlineOnly=true`);
  };

  // Submit device data
  const handleSubmitDevice = async (item: any) => {
    const itemId = item.id;
    const itemIdStr = itemId.toString();
    setDeviceSubmitStates((prev) => ({
      ...prev,
      [itemIdStr]: { loading: true, success: false, error: null },
    }));

    try {
      const deviceData = {
        backflow_type: item.backflow_type,
        backflow_size: item.backflow_size,
        backflow_make: item.backflow_make,
        backflow_model: item.backflow_model,
        backflow_serial_no: item.backflow_serial_no,
        meter_size: item.meter_size,
        water_meter_make: item.water_meter_make,
        water_meter_no: item.water_meter_no,
        system_no: item.system_no,
        location: item.location,
        asset_no: item.asset_no,
        status: item.status,
        emp_id: item.emp_id,
      };
      await axios.put(`/backflows/backflow_job_items/${itemId}`, deviceData);

      markDeviceSynced(jobGroupId, itemId);

      setDeviceSubmitStates((prev) => ({
        ...prev,
        [itemIdStr]: { loading: false, success: true, error: null },
      }));

      setSnackBarMessage('Device data submitted successfully');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);

      if (!isJobGroupCached(jobGroupId)) {
        setSnackBarMessage('All data submitted! Cache cleared.');
        handleSnackbarAlertSeverityChange('success');
        setSnackBarOpen(true);
        onSubmitSuccess();
        onClose();
      }
    } catch (error: any) {
      setDeviceSubmitStates((prev) => ({
        ...prev,
        [itemIdStr]: { loading: false, success: false, error: error.message || 'Failed to submit' },
      }));

      setSnackBarMessage('Failed to submit device data');
      handleSnackbarAlertSeverityChange('error');
      setSnackBarOpen(true);
    }
  };

  // Submit log data
  const handleSubmitLog = async (item: any) => {
    const itemId = item.id;
    const itemIdStr = itemId.toString();

    setLogSubmitStates((prev) => ({
      ...prev,
      [itemIdStr]: { loading: true, success: false, error: null },
    }));

    try {
      const logData = { ...item.backflow_log };

      // Remove metadata fields
      delete logData.savedAt;
      delete logData.needsSync;
      delete logData.id;

      await axios.put(`/backflows/backflow_logs/${itemId}`, logData);

      markLogSynced(jobGroupId, itemId);

      setLogSubmitStates((prev) => ({
        ...prev,
        [itemIdStr]: { loading: false, success: true, error: null },
      }));

      setSnackBarMessage('Test log submitted successfully');
      handleSnackbarAlertSeverityChange('success');
      setSnackBarOpen(true);

      if (!isJobGroupCached(jobGroupId)) {
        setSnackBarMessage('All data submitted! Cache cleared.');
        handleSnackbarAlertSeverityChange('success');
        setSnackBarOpen(true);
        onSubmitSuccess();
        onClose();
      }
    } catch (error: any) {
      setLogSubmitStates((prev) => ({
        ...prev,
        [itemIdStr]: { loading: false, success: false, error: error.message || 'Failed to submit' },
      }));

      setSnackBarMessage('Failed to submit test log');
      handleSnackbarAlertSeverityChange('error');
      setSnackBarOpen(true);
    }
  };

  // Render device data summary
  const renderDeviceData = (item: any) => {
    const itemId = item.id.toString();
    const submitState = deviceSubmitStates[itemId];

    // Show if it needs sync OR if it has been successfully submitted
    if (!needsDeviceSync(item) && !submitState?.success) return null;

    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Left side - Data */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
            Device Info
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Type: <strong>{item.backflow_type || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Make: <strong>Under development</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Serial: <strong>{item.serial_no || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Asset No: <strong>{item.asset_no || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Location: <strong>{item.location || 'N/A'}</strong>
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Right side - Submit button */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '160px', justifyContent: 'flex-end' }}>
          {submitState?.success ? (
            <Chip label="✓ Submitted" color="success" size="small" />
          ) : submitState?.error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Chip label="✗ Failed" color="error" size="small" />
              <Button
                variant="contained"
                size="small"
                onClick={() => handleSubmitDevice(item)}
                disabled={submitState?.loading}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSubmitDevice(item)}
              disabled={submitState?.loading}
              startIcon={submitState?.loading && <CircularProgress size={16} />}
            >
              {submitState?.loading ? 'Submitting...' : 'Submit Device Data'}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  // Render log data summary
  const renderLogData = (item: any) => {
    const log = item.backflow_log;
    if (!log) return null; // Don't show if no log data exists

    const itemId = item.id.toString();
    const submitState = logSubmitStates[itemId];

    // Show if it needs sync OR if it has been successfully submitted
    if (!needsLogSync(item) && !submitState?.success) return null;

    return (
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        {/* Left side - Data */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 1 }}>
            Test Log
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Size: <strong>{log.backflow_size || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Line Pressure PSI: <strong>{log.line_pressure_psi || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Valve 1 Tight: <strong>{log.test1_valve_tight || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Gauge 1: <strong>{log.test1_gauge_value || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Valve 2 Tight: <strong>{log.test2_valve_tight || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Gauge 2: <strong>{log.test2_gauge_value || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Relief Valve Opened: <strong>{log.test3_valve_open || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="body2" color="text.secondary">
                Relief Valve Pressure: <strong>{log.test3_relief_valve_pressure || 'N/A'}</strong>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                Comment: <em>{log.internal_comment}</em>
              </Typography>
            </Grid>
          </Grid>
        </Box>

        {/* Right side - Submit button */}
        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '140px', justifyContent: 'flex-end' }}>
          {submitState?.success ? (
            <Chip label="✓ Submitted" color="success" size="small" />
          ) : submitState?.error ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, alignItems: 'flex-end' }}>
              <Chip label="✗ Failed" color="error" size="small" />
              <Button
                variant="contained"
                size="small"
                onClick={() => handleSubmitLog(item)}
                disabled={submitState?.loading}
              >
                Retry
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              size="small"
              onClick={() => handleSubmitLog(item)}
              disabled={submitState?.loading}
              startIcon={submitState?.loading && <CircularProgress size={16} />}
            >
              {submitState?.loading ? 'Submitting...' : 'Submit Test Log'}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Review & Submit Cached Data
        <Typography variant="body2" color="text.secondary">
          Order {jobGroupData?.jobGroup?.order_no} - {jobGroupData?.jobGroup?.customer_name}
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        {!navigator.onLine && (
          <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="body2">
              ⚠️ You appear to be offline. Please connect to the internet before submitting.
            </Typography>
          </Box>
        )}

        {jobGroupData?.jobItems?.map((item: any, index: number) => (
          <Box key={item.id}>
            {/* Job Item Header with Edit Button */}
            <Box
              sx={{
                mb: 2,
                mt: index > 0 ? 3 : 0,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Box>
                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 700, color: 'primary.main' }}>
                  {item.asset_no || 'No Asset Number'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.location || 'Location not specified'}
                </Typography>
              </Box>

              {/* Edit button for this item */}
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleEditItem(item.id)}
                startIcon={<EditIcon />}
                sx={{ bgcolor: 'white' }}
              >
                Edit
              </Button>
            </Box>

            {/* Device and Log Cards */}
            <Box sx={{ mb: 3 }}>
              {renderDeviceData(item)}
              {renderLogData(item)}
            </Box>

            {/* Divider between items (not after last item) */}
            {index < jobGroupData.jobItems.length - 1 && <Divider sx={{ my: 3 }} />}
          </Box>
        ))}

        {!jobGroupData?.jobItems?.some((item: any) => needsDeviceSync(item) || needsLogSync(item)) && (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body1" color="text.secondary">
              This job group has no pending offline changes.
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

export default SubmitCachedDataModal;
